import React from 'react';
import { CartesianGrid, Line, LineChart, XAxis } from 'recharts';

import { Trend, TrendValue } from '../trend-value';

import { Card, CardContent, CardHeader } from '@/components/ui/card';
import {
  ChartConfig,
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from '@/components/ui/chart';
import { formatNumber } from '@/lib/formatNumber';
import { useChartDateFormatters } from '@/lib/useChartDateFormatters';
import { cn } from '@/lib/utils';

export interface DiscoveriesCreatedProps {
  total: number;
  trend: Trend;
  trendValue: string;
  subBreakdowns: Array<{ label: string; value: number }>;
  sourceTotals: Array<{ key: string; label: string; value: number }>;
  data: Array<Record<string, number | string | Date>>;
  chartConfig: ChartConfig;
  locale?: string;
  className?: string;
}

const formatWhole = (value: number) => formatNumber(value, {});

export function DiscoveriesCreated({
  total,
  trend,
  trendValue,
  subBreakdowns,
  sourceTotals,
  data,
  chartConfig,
  locale,
  className,
}: DiscoveriesCreatedProps) {
  const { tickFormatter, formatXLabel } = useChartDateFormatters(locale);
  const lineKeys = Object.keys(chartConfig);

  return (
    <Card className={cn('shadow-none border border-dark-300 rounded-lg', className)}>
      <CardHeader className="p-0 space-y-0 border-b border-dark-300">
        <div className="flex items-stretch justify-between">
          <div className="flex flex-col gap-1 justify-center py-4 px-4">
            <p className="uppercase heading-xxxs font-semibold text-dark tracking-normal">Discoveries created</p>
            <div className="flex items-center gap-2">
              <span className="font-mono text-3xl leading-10 text-dark">{formatWhole(total)}</span>
              <TrendValue trend={trend} trendValue={trendValue} />
              {subBreakdowns.map((item, i) => (
                <span key={item.label} className="text-xs leading-4 text-dark-600">
                  {i > 0 && <span className="mr-2">&middot;</span>}
                  {formatWhole(item.value)} {item.label}
                </span>
              ))}
            </div>
          </div>

          <div className="flex items-center self-stretch">
            {sourceTotals.map((source) => (
              <div
                key={source.key}
                className="border-l border-dark-300 flex flex-col justify-center px-4 py-2 self-stretch"
              >
                <span className="text-xs leading-4 text-dark-700">{source.label}</span>
                <span className="font-mono text-3xl leading-10 text-dark">{formatWhole(source.value)}</span>
              </div>
            ))}
          </div>
        </div>
      </CardHeader>

      <CardContent className="px-4 pt-4 pb-0">
        <ChartContainer config={chartConfig} className="h-48 w-full">
          <LineChart data={data} margin={{ top: 8, left: 0, right: 0, bottom: 0 }}>
            <CartesianGrid horizontal vertical={false} className="stroke-dark-300" />
            <XAxis
              dataKey="date"
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              minTickGap={32}
              interval="preserveStartEnd"
              tickFormatter={tickFormatter}
              tick={({ x, y, payload }) => (
                <text x={x} y={y} dy={12} textAnchor="middle" className="text-xs fill-dark-600">
                  {tickFormatter(payload.value)}
                </text>
              )}
            />
            <ChartTooltip
              cursor={false}
              content={({ active, payload, label }) => (
                <ChartTooltipContent active={active} payload={payload} label={formatXLabel(label)} />
              )}
            />
            {lineKeys.map((key) => (
              <Line
                key={key}
                dataKey={key}
                type="monotone"
                stroke={`var(--color-${key})`}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4, strokeWidth: 0 }}
              />
            ))}
            <ChartLegend content={<ChartLegendContent />} />
          </LineChart>
        </ChartContainer>
      </CardContent>
    </Card>
  );
}
